import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";

// Create the context
const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

// Detect environment
const isDev = process.env.NODE_ENV === "development";

// Resolve base API URL
// In dev: always prefer the local proxy unless an explicit dev override is set.
// In prod: use the deployed API env values.
const DEV_DEFAULT = "/api";
const DEV_BASE = process.env.REACT_APP_DEV_API_BASE_URL || DEV_DEFAULT;
const PROD_BASE =
  process.env.REACT_APP_API_URL || "";
const BASE_ENV = isDev ? DEV_BASE : PROD_BASE;

// Helper to safely join base + path
const joinUrl = (base, path) => {
  const b = (base || "").replace(/\/+$/, "");
  let p = path || "";
  if (!p.startsWith("/")) p = "/" + p;

  if (b.endsWith("/api") && p.startsWith("/api/")) {
    p = p.replace(/^\/api/, "");
  }

  return b + p;
};

export default function AuthProvider({ children }) {
  const [token, setToken] = useState(
    () => localStorage.getItem("fq_token") || null
  );

  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("fq_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (token) localStorage.setItem("fq_token", token);
    else localStorage.removeItem("fq_token");

    if (user) {
      try {
        localStorage.setItem("fq_user", JSON.stringify(user));
      } catch {
        // ignore storage quota / JSON error
      }
    } else {
      localStorage.removeItem("fq_user");
    }
  }, [token, user]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "fq_token") setToken(e.newValue || null);
      if (e.key === "fq_user") {
        try {
          setUser(e.newValue ? JSON.parse(e.newValue) : null);
        } catch {
          setUser(null);
        }
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const api = useCallback(
    async (path, opts = {}) => {
      const base = BASE_ENV;
      const headers = {
        "Content-Type": "application/json",
        ...(opts.headers || {}),
      };

      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(joinUrl(base, path), {
        ...opts,
        headers,
        credentials: "include",
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        // ignore non-JSON responses
      }

      if (!res.ok) {
        const msg =
          data?.error || data?.message || `Request failed (${res.status})`;
        throw new Error(msg);
      }

      return data;
    },
    [token]
  );

  const continueAsGuest = useCallback(async () => {
    const data = await api("/api/auth/guest", { method: "POST" });
    setToken(data.token);
    setUser({ ...data.user, isGuest: true });
  }, [api]);

  const register = useCallback(
    async (username, email, password, firstName, lastName) => {
      const data = await api("/api/auth/register", {
        method: "POST",
        body: JSON.stringify({
          username,
          email,
          password,
          first_name: firstName,
          last_name: lastName,
        }),
      });
      setToken(data.token);
      setUser(data.user);
    },
    [api]
  );

  const login = useCallback(
    async (username, password) => {
      const data = await api("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      setToken(data.token);
      setUser(data.user);
    },
    [api]
  );

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("fq_token");
    localStorage.removeItem("fq_user");
  }, []);

  useEffect(() => {
    const handleWindowUnload = () => {
      localStorage.removeItem("fq_token");
      localStorage.removeItem("fq_user");
    };

    window.addEventListener("beforeunload", handleWindowUnload);
    return () => window.removeEventListener("beforeunload", handleWindowUnload);
  }, []);

  const value = useMemo(
    () => ({
      token,
      user,
      setToken,
      setUser,
      continueAsGuest,
      register,
      login,
      logout,
      api,
    }),
    [token, user, continueAsGuest, register, login, logout, api]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}